const DB_NAME = 'leela-recording-audio-v1';
const STORE = 'segments';
const MAX_PENDING_BYTES = 64 * 1024 * 1024;

export class AudioQueueError extends Error {
  constructor(code) { super(code); this.code = code; }
}

export const openAudioQueue = (idb = typeof indexedDB === 'undefined' ? null : indexedDB) => {
  if (!idb) return Promise.reject(new AudioQueueError('AUDIO_OFFLINE_STORAGE_UNAVAILABLE'));
  return new Promise((resolve, reject) => {
    const req = idb.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const database = req.result;
      if (!database.objectStoreNames.contains(STORE)) {
        const store = database.createObjectStore(STORE, { keyPath: 'uploadId' });
        store.createIndex('recordingId', 'recordingId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new AudioQueueError('AUDIO_OFFLINE_STORAGE_FAILED'));
    req.onblocked = () => reject(new AudioQueueError('AUDIO_OFFLINE_STORAGE_BLOCKED'));
  });
};

const operation = (db, mode, action) => new Promise((resolve, reject) => {
  const tx = db.transaction(STORE, mode);
  const result = action(tx.objectStore(STORE));
  tx.oncomplete = () => resolve(result.result);
  tx.onerror = () => reject(tx.error || new AudioQueueError('AUDIO_QUEUE_FAILED'));
  tx.onabort = () => reject(tx.error || new AudioQueueError('AUDIO_QUEUE_ABORTED'));
});

export const listPendingAudio = async (db, recordingId = null) => {
  const all = await operation(db, 'readonly', (store) => store.getAll());
  return all.filter((row) => !recordingId || row.recordingId === recordingId)
    .sort((a,b) => a.createdAt - b.createdAt);
};

export const enqueueAudio = async (db, entry) => {
  if (!entry?.blob || !entry.uploadId || !entry.recordingId) throw new AudioQueueError('AUDIO_QUEUE_INVALID_SEGMENT');
  const existing = await listPendingAudio(db);
  if (existing.reduce((sum,row) => sum + row.blob.size, 0) + entry.blob.size > MAX_PENDING_BYTES) {
    throw new AudioQueueError('AUDIO_QUEUE_FULL');
  }
  return operation(db, 'readwrite', (store) => store.put({
    ...entry, createdAt: entry.createdAt || Date.now(),
  }));
};

export const removePendingAudio = (db, uploadId) =>
  operation(db, 'readwrite', (store) => store.delete(uploadId));

export const uploadAudioSegment = async ({ entry, gameId, token, signal, apiUrl }) => {
  if (!token || !gameId) throw new AudioQueueError('AUDIO_AUTH_REQUIRED');
  const response = await fetch(apiUrl('/api/games/' + gameId + '/recording/audio/segment'), {
    method: 'POST', signal,
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/octet-stream',
      'X-Recording-Id': entry.recordingId,
      'X-Capture-Id': entry.captureId,
      'X-Upload-Id': entry.uploadId,
      'X-Audio-Start-Ms': String(entry.startMs),
      'X-Audio-Duration-Ms': String(entry.durationMs),
      'X-Audio-Mime-Type': entry.mimeType,
      'X-Audio-Sources': (entry.sourceIdentities || []).join(','),
    },
    body: entry.blob,
  });
  if (!response.ok) {
    let code = '';
    try { code = (await response.json())?.code || ''; } catch {}
    const error = new AudioQueueError(code || 'AUDIO_UPLOAD_HTTP_' + response.status);
    error.status = response.status;
    throw error;
  }
  return response.json();
};
